import React, { useCallback, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlay,
  faCirclePause,
  faXmark,
  faDiagramProject,
} from '@fortawesome/free-solid-svg-icons';
import Chart from 'react-apexcharts';
import axios from 'axios';
import ResponsiveTable from './ResponsiveTable';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function groupTopPlatforms(items, n = 7) {
  const sorted = [...items].sort((a, b) => b.platformAmount - a.platformAmount);
  if (sorted.length <= n) return sorted;
  const etcAmount = sorted.slice(n).reduce((s, r) => s + r.platformAmount, 0);
  return [...sorted.slice(0, n), { platformType: '기타', platformAmount: etcAmount }];
}

const PLATFORM_HORIZ_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: 'end' } } },
  xaxis: { categories, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { style: { fontSize: '12px', colors: '#94a3b8' }, maxWidth: 130 } },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetX: -6,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 0, right: 70 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
});

const UNIFIED_BAR_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  xaxis: {
    categories,
    labels: { style: { fontSize: '12px', colors: '#94a3b8' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetY: -20,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  plotOptions: {
    bar: { dataLabels: { position: 'top' }, borderRadius: 4 },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: { height: 250, toolbar: { show: false } },
        yaxis: [{ labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } }],
        grid: { padding: { left: 24 } },
      },
    },
  ],
});

const ChartLoading = () => (
  <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-16">
    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-sky-500" />
    <span>불러오는 중...</span>
  </div>
);

function BySongsTable({ data, totalCount, currentPage, pageSize, me, month }) {
  const { playImmediate, playlist, currentIndex, isPlaying, togglePlay } = useMusicPlayer();
  const [selectedSong, setSelectedSong] = useState(null);
  const [monthlyAmounts, setMonthlyAmounts] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [pendingSongId, setPendingSongId] = useState(null);

  const [selectedChannelSong, setSelectedChannelSong] = useState(null);
  const [platformAmounts, setPlatformAmounts] = useState([]);

  const currentTrack = currentIndex !== null ? playlist[currentIndex] : null;

  const handlePlay = useCallback(
    (row) => {
      const song = row.original.song;
      if (!song?.fileUrl) return;
      // 이미 이 곡이 재생 중이면 토글
      if (currentTrack && currentTrack.id === song.id) {
        togglePlay();
        return;
      }
      playImmediate([{
        id: song.id,
        nameKr: song.nameKr,
        artistKr: row.original.album?.artist?.nameKr || '',
        fileUrl: song.fileUrl,
        albumImage: row.original.album?.image || FALLBACK_IMG,
        albumName: row.original.album?.nameKr || '',
      }]);
    },
    [currentTrack, playImmediate, togglePlay]
  );

  const fetchMonthlyAmounts = useCallback(async (songId) => {
    try {
      const res = await axios.get(`/BySong/getMonthlyAmounts`, {
        params: { songId },
      });
      setMonthlyAmounts(res.data);
    } catch (err) {
      console.error('정산 데이터 불러오기 오류:', err);
    } finally {
      setLoadingChart(false);
    }
  }, []);

  const handleSongClick = useCallback(
    (song) => {
      if (!song?.songId) {
        console.warn('songId가 준비되지 않았습니다.');
        return;
      }
      setSelectedSong(song);
      setMonthlyAmounts([]);
      setLoadingChart(true);
      fetchMonthlyAmounts(song.songId);
    },
    [fetchMonthlyAmounts]
  );

  useEffect(() => {
    if (pendingSongId) {
      fetchMonthlyAmounts(pendingSongId);
      setPendingSongId(null);
    }
  }, [pendingSongId, fetchMonthlyAmounts]);

  const handleChannelClick = useCallback(
    async (song) => {
      if (!song?.songId) {
        console.warn('songId가 준비되지 않았습니다.');
        return;
      }
      setSelectedChannelSong(song);
      setPlatformAmounts([]);
      setLoadingChart(true);
      try {
        const monthYd = month?.slice(0, 7);
        const res = await axios.get(`/ByPlatform/byMonthPlatformsBySong`, {
          params: { songId: song.songId, monthYd },
        });
        setPlatformAmounts(res.data.items);
      } catch (err) {
        console.error('플랫폼 정산 데이터 오류:', err);
      } finally {
        setLoadingChart(false);
      }
    },
    [month]
  );

  const columns = React.useMemo(() => {
    if (!me) return [];

    return [
      {
        Header: () => <div className="whitespace-nowrap text-center">순번</div>,
        id: 'row',
        Cell: ({ row }) => (
          <div className="text-center whitespace-nowrap">
            {totalCount - ((currentPage - 1) * pageSize + row.index)}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="text-center items-center justify-center whitespace-nowrap">
            듣기
          </div>
        ),
        accessor: 'song.fileUrl',
        Cell: ({ value, row }) => {
          const songId = row.original.song?.id;
          const isCurrent = currentTrack && currentTrack.id === songId && isPlaying;
          return value ? (
            <span
              className="inline-flex items-center justify-center cursor-pointer text-2xl whitespace-nowrap"
              onClick={() => handlePlay(row)}
              title={isCurrent ? '일시정지' : '재생'}>
              <FontAwesomeIcon
                icon={isCurrent ? faCirclePause : faCirclePlay}
                className={
                  isCurrent
                    ? 'text-red-400 hover:text-red-700'
                    : 'text-sky-500 hover:text-sky-700'
                }
              />
            </span>
          ) : (
            <span className="text-gray-400 whitespace-nowrap">-</span>
          );
        },
      },
      {
        Header: () => <div className="text-center whitespace-nowrap">채널</div>,
        id: 'channel',
        Cell: ({ row }) => (
          <span
            className="text-purple-500 text-xl cursor-pointer whitespace-nowrap"
            title="플랫폼별 정산 보기"
            onClick={() => handleChannelClick(row.original)}>
            <FontAwesomeIcon icon={faDiagramProject} />
          </span>
        ),
      },
      {
        Header: '곡명',
        accessor: 'song.nameKr',
        Cell: ({ row }) => (
          <span
            className="font-bold text-sky-600 cursor-pointer"
            onClick={() => handleSongClick(row.original)}>
            {row.original.song.nameKr}
          </span>
        ),
      },
      {
        Header: '앨범명',
        accessor: 'album.nameKr',
      },
      {
        Header: '아티스트',
        accessor: 'album.artist.nameKr',
      },
      ...(me?.adminYn === 'Y'
        ? [
            {
              Header: '권리사',
              accessor: 'album.displayClient',
              Cell: ({ value }) => <span>{value || '-'}</span>,
            },
          ]
        : []),
      {
        Header: '총 히트수',
        accessor: 'songHitCount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '총 정산액',
        accessor: 'songAmount',
        Cell: ({ value }) => (
          <span className="font-bold">{Number(value).toLocaleString()}</span>
        ),
      },
    ];
  }, [
    totalCount,
    currentPage,
    pageSize,
    currentTrack,
    isPlaying,
    handlePlay,
    handleSongClick,
    handleChannelClick,
    me,
  ]);

  return (
    <>
      <ResponsiveTable
        columns={columns}
        data={data}
        visibleOnMobile={[
          'row',
          'song.nameKr',
          'album.artist.nameKr',
          'songAmount',
        ]}
      />

      {/* 월별 정산 모달 */}
      {selectedSong && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedSong(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedSong(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedSong.song.nameKr}" 최근 1년 정산 그래프
            </h2>
            {loadingChart ? (
              <ChartLoading />
            ) : (
              <Chart
                type="bar"
                width="100%"
                height="300"
                series={[
                  {
                    name: '월별 정산액',
                    data: monthlyAmounts.map((item) =>
                      me?.adminYn === 'Y' ? item.amount : item.clientAmount
                    ),
                  },
                ]}
                options={UNIFIED_BAR_OPTIONS(
                  monthlyAmounts.map((item) => item.month),
                  'song-amount-monthly-chart'
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* 플랫폼 모달 */}
      {selectedChannelSong && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedChannelSong(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedChannelSong(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedChannelSong.song.nameKr}" 플랫폼별 정산 그래프
            </h2>
            {loadingChart ? (
              <ChartLoading />
            ) : (() => {
              const grouped = groupTopPlatforms(platformAmounts);
              return (
                <Chart
                  type="bar"
                  width="100%"
                  height={Math.max(260, grouped.length * 42)}
                  series={[{ name: '정산액', data: grouped.map((item) => item.platformAmount) }]}
                  options={PLATFORM_HORIZ_OPTIONS(grouped.map((item) => item.platformType), 'song-platform-chart')}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default BySongsTable;
